'use client';

import { useRender } from '@base-ui-components/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils/cn';
import './button.css';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-button text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-surface-button text-foreground',
        primary: 'bg-primary text-primary-foreground',
        destructive: 'bg-destructive text-destructive-foreground',
        outline: 'border border-border bg-transparent',
        secondary: 'bg-secondary text-secondary-foreground',
        ghost: 'bg-transparent',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  render?:
    | React.ReactElement
    | ((
        props: React.ButtonHTMLAttributes<HTMLButtonElement>
      ) => React.ReactElement);
}

function Button({
  className,
  variant,
  size,
  render: renderProp,
  ref,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) {
  const isFlat = variant === 'ghost' || variant === 'link';

  const mergedProps = {
    className: cn(
      isFlat ? 'button-base-flat' : 'button-base',
      buttonVariants({ variant, size }),
      className
    ),
    ...props,
  };

  return useRender({
    render: renderProp ?? <button />,
    ...(ref !== undefined ? { ref } : {}),
    props: mergedProps,
  });
}

export { Button, buttonVariants };
export type { ButtonProps };
